/** Session key for clinic / services / stocks / vets cache. */
export const MANDATE_STORAGE_KEY = "mandateData";

/** ~3 refresh cycles per day: mandate APIs run at most once per this interval. */
export const MANDATE_STALE_MS = 8 * 60 * 60 * 1000;

/**
 * Drop these first when sessionStorage quota is exceeded.
 * Keep clinic / dataSharing / vets / stocks / services as long as possible.
 */
const DROP_ON_QUOTA_ORDER = [
  "inpatients",
  "categories",
  "vetUnavailability",
  "clinicHolidays",
  "serviceRemainders",
  "facilities",
  "paymentMethods",
  "bins",
  "locations",
  "clinicHours",
  "clinicRoles",
  "clinicBranches",
  "diagnosticTemplates",
  "certificates",
  "stocks",
  "services",
  "vets",
];

/** Top-level / nested keys that inflate mandate JSON but are unused by session consumers. */
const HEAVY_DROP_KEYS = new Set([
  "image",
  "images",
  "photo",
  "photos",
  "avatar",
  "logo",
  "attachment",
  "attachments",
  "document",
  "documents",
  "fileUrl",
  "fileUrls",
  "thumbnail",
  "thumbnails",
  "signature",
  "signatures",
  "base64",
  "htmlContent",
  "richText",
  "supplierDetails",
  "supplierInvoiceDetails",
]);

const isQuotaExceeded = (err) =>
  err?.name === "QuotaExceededError" ||
  err?.code === 22 ||
  err?.code === 1014 ||
  /quotaexceeded|exceeded the quota/i.test(String(err?.message ?? ""));

/**
 * Recursively drop known heavy media/blob keys. Keeps arrays/objects otherwise intact
 * so billing/workbook nested fields (stockBatch, serviceVariants, etc.) stay usable.
 */
export const slimMandateValue = (value, depth = 0) => {
  if (value == null || depth > 8) return value;
  if (Array.isArray(value)) {
    return value.map((item) => slimMandateValue(item, depth + 1));
  }
  if (typeof value !== "object") return value;

  const out = {};
  for (const [key, child] of Object.entries(value)) {
    if (HEAVY_DROP_KEYS.has(key)) continue;
    out[key] = slimMandateValue(child, depth + 1);
  }
  return out;
};

const slimMandatePayload = (data) => {
  if (!data || typeof data !== "object") return data ?? {};
  const out = { ...data };
  for (const key of Object.keys(out)) {
    if (key === "lastUpdatedAt") continue;
    // Keep clinic profile intact (logo, footer, address) for forms/print screens.
    if (key === "clinic") continue;
    out[key] = slimMandateValue(out[key]);
  }
  return out;
};

export const readMandateSession = () => {
  try {
    const raw = sessionStorage.getItem(MANDATE_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const readLastUpdatedMs = () => {
  const parsed = readMandateSession();
  const ts = parsed?.lastUpdatedAt;
  if (!ts) return null;
  const t = new Date(ts).getTime();
  return Number.isNaN(t) ? null : t;
};

/** True when there is no timestamp or data is older than {@link MANDATE_STALE_MS}. */
export const isMandateStale = () => {
  const updatedAt = readLastUpdatedMs();
  if (updatedAt == null) return true;
  return Date.now() - updatedAt >= MANDATE_STALE_MS;
};

/** Milliseconds until the next scheduled refresh from stored `lastUpdatedAt`. */
export const msUntilNextMandateRefresh = () => {
  const updatedAt = readLastUpdatedMs();
  if (updatedAt == null) return 0;
  return Math.max(0, MANDATE_STALE_MS - (Date.now() - updatedAt));
};

const notifyMandateSessionUpdated = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("mandate-session-updated"));
  }
};

/**
 * Persist mandate payload safely. On QuotaExceededError, progressively drop
 * secondary keys so the app keeps critical clinic/catalog data instead of crashing.
 */
const persistMandatePayload = (payload) => {
  const tryWrite = (obj) => {
    sessionStorage.setItem(MANDATE_STORAGE_KEY, JSON.stringify(obj));
    notifyMandateSessionUpdated();
    return obj;
  };

  let next = slimMandatePayload(payload);

  try {
    return tryWrite(next);
  } catch (err) {
    if (!isQuotaExceeded(err)) {
      console.error("Failed to write mandateData:", err);
      return next;
    }
  }

  for (const key of DROP_ON_QUOTA_ORDER) {
    if (!(key in next)) continue;
    const { [key]: _dropped, ...rest } = next;
    next = rest;
    try {
      console.warn(
        `mandateData quota exceeded — dropped "${key}" and retrying session write.`,
      );
      return tryWrite(next);
    } catch (err) {
      if (!isQuotaExceeded(err)) {
        console.error("Failed to write mandateData after shrink:", err);
        return next;
      }
    }
  }

  // Absolute last resort: clear key and store a minimal stub so later patches can rebuild.
  try {
    sessionStorage.removeItem(MANDATE_STORAGE_KEY);
    const minimal = {
      ...(next.clinic ? { clinic: next.clinic } : {}),
      ...(next.dataSharing ? { dataSharing: next.dataSharing } : {}),
      lastUpdatedAt: next.lastUpdatedAt ?? new Date().toISOString(),
    };
    return tryWrite(minimal);
  } catch (err) {
    console.error(
      "Failed to write mandateData even after clearing secondary data:",
      err,
    );
    try {
      sessionStorage.removeItem(MANDATE_STORAGE_KEY);
    } catch {
      // ignore
    }
    return next;
  }
};

/**
 * Save full mandate payload with a fresh `lastUpdatedAt` (ISO string).
 * All countdown logic keys off this field — use the same helper anywhere session mandate data is replaced.
 */
export const writeMandateSession = (data) => {
  const payload = {
    ...data,
    lastUpdatedAt: new Date().toISOString(),
  };
  return persistMandatePayload(payload);
};

/**
 * Merge into existing session and bump `lastUpdatedAt` (e.g. one screen refreshed only vets).
 */
export const patchMandateSession = (partial) => {
  const prev = readMandateSession() ?? {};
  return writeMandateSession({ ...prev, ...partial });
};

/**
 * Merge fields without bumping `lastUpdatedAt` (e.g. dataSharing-only updates).
 * Still uses quota-safe persistence.
 */
export const mergeMandateSessionKeepTimestamp = (partial) => {
  const prev = readMandateSession() ?? {};
  const payload = {
    ...prev,
    ...partial,
    ...(prev.lastUpdatedAt ? { lastUpdatedAt: prev.lastUpdatedAt } : {}),
  };
  return persistMandatePayload(payload);
};
